import React, { useContext, useMemo } from 'react';
import { Box, Divider, Menu, Typography } from '@mui/material';
import { ContentWrapper, CustomAvatar, profileMenuProps, UserNameBox } from './HTProfileMenuStyled';
import { useNavigate } from 'react-router';
import { logoutMember } from '@/state/member/useMember';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { PrivateRouteContext } from '@/context/PrivateRouteContext';
import { useTranslation } from 'react-i18next';
import { ProfileMenuItem } from '@/state/general/generalTypes';
import { ROUTES } from '@/lib/constants';
import WalletDisplaySwitcher from '@/components/Hypertracker/WalletDisplaySwitcher';

interface ProfileMenuInterface {
    anchorEl: null | HTMLElement;
    handleClose: () => void;
    open: boolean;
    avatarLetter: string;
    closeDrawer?: () => void;
}

const HTProfileMenu: React.FC<React.PropsWithChildren<ProfileMenuInterface>> = ({
    anchorEl,
    handleClose,
    open,
    avatarLetter,
    closeDrawer,
}) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'profile_menu_drawer' });
    const { isReturnBack } = useContext(PrivateRouteContext);
    const member = useSelector((state: RootState) => selectMemberProfile(state));

    const profileMenu: ProfileMenuItem[] = useMemo(() => {
        return [
            {
                name: t('account_settings'),
                isExternal: false,
                url: ROUTES.PROFILE_SETTINGS,
                separator: false,
            },
            {
                name: t('manage_connections'),
                isExternal: false,
                url: ROUTES.HYPERTRACKER_CONNECTIONS,
                separator: false,
            },
            {
                name: t('go_to_cmm'),
                isExternal: false,
                url: '/',
                separator: false,
            },
            {
                name: t('pricing'),
                isExternal: false,
                url: ROUTES.HYPERTRACKER_PRICING,
                separator: true,
            },
            {
                name: t('api'),
                isExternal: false,
                url: ROUTES.HYPERTRACKER_API,
                separator: true,
            },
            {
                name: t('logout'),
                isExternal: false,
                url: 'logout',
                separator: true,
            },
        ];
    }, [member.accountType, i18n.language]);

    const onElementClick = (menuItem) => {
        if (menuItem.url === 'logout') {
            isReturnBack(false);
            logoutMember(true, handleClose)
                .then(() => {
                    console.log('ok');
                })
                .catch((error) => {
                    console.log('err', error);
                    return;
                });

            return;
        }

        if (menuItem.isExternal && menuItem.url) {
            const newWindow = window.open();
            if (newWindow) {
                newWindow.location = menuItem.url;
            }
            return;
        }

        if (!menuItem.isExternal && menuItem.url) {
            navigate(menuItem.url);
        }

        if (closeDrawer) {
            closeDrawer();
        }
        handleClose();
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: profileMenuProps }}
        >
            <ContentWrapper>
                <UserNameBox>
                    <CustomAvatar>{avatarLetter}</CustomAvatar>

                    <Typography variant="p12" color="customText.text1">
                        {member.username}
                    </Typography>
                </UserNameBox>
                <Divider
                    orientation="horizontal"
                    sx={{ margin: '8px 0', width: '100%', height: '1px' }}
                />
                {profileMenu.map((menuItem, catIndex) => (
                    <Box key={catIndex}>
                        {menuItem.url === 'logout' && (
                            <>
                                <Divider sx={{ margin: '8px 0' }} />
                                <WalletDisplaySwitcher onLoginPrompt={handleClose} />
                            </>
                        )}
                        {menuItem.separator && <Divider sx={{ margin: '8px 0' }} />}
                        <Typography
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    color: (theme) =>
                                        menuItem.url === 'logout'
                                            ? theme.palette.error.dark
                                            : menuItem.url === ROUTES.HYPERTRACKER_API
                                              ? '#FFAB00'
                                              : theme.palette.hyperLiquidColor,
                                },
                                textDecoration: 'none',
                            }}
                            variant="p12"
                            color={
                                menuItem.url === 'logout'
                                    ? 'error.main'
                                    : menuItem.url === ROUTES.HYPERTRACKER_API
                                      ? '#FFAB00'
                                      : 'common.white'
                            }
                            onClick={() => {
                                onElementClick(menuItem);
                            }}
                            component={'a'}
                        >
                            {menuItem.name}
                        </Typography>
                    </Box>
                ))}
            </ContentWrapper>
        </Menu>
    );
};

export default HTProfileMenu;
